import {
  ApiTypesEnum,
  CarrierOrGroupEnum,
  CurrencyLockTypeEnum,
  FindCarrierTypeEnum,
  SelectedItemTypeEnum,
} from '@api/ringil-api.v2.tsx';
import type {
  CheckDuplicationExpeditionResponseOutDTO,
  DachserDeliveryTermEnum,
  DachserProductTypeEnum,
  DuplicationMoreReservationTimeslotsDTO,
  SaveDistributionRequestInDTO,
  SchenkerAddonsTypeEnum,
  SchenkerProductTypeEnum,
  TimeslotCalendarDTO,
  UserInfoOutDTO,
} from '@api/ringil-api.v2.tsx';
import {
  CarrierDistributionSelectorData,
  DistributionType,
  getSelectedUserIds,
  resolveResponseDeadline,
} from '@components/RingilComponents/CarrierDistributionSelector/CarrierDistributionSelector.tsx';
import {
  apiPragueFromDayjsLocalWithFormat,
  apiUtcFromDayjs,
  dayjsFromApiUtcInTz,
  dayjsNowInTz,
  stringHHmmFromStringInTz,
} from '@utils/dateWithTz.tsx';
import { isNotNullOrUndefined } from '@utils/utils.tsx';

/**
 * RD-3062 — the form ↔ API mapping of `DuplicateExpeditionSidebar`, moved out of the component unchanged so
 * it can be unit-tested. The component keeps the hooks, API calls and UI (including the inline handlers that
 * seed a new timeslot); everything here is pure.
 */

export interface Form {
  duplicateReservations: boolean;
  copyAttachments: boolean;
  fromExpeditionId: string;
  copyToLocalDates: string[];
  distribution?: CarrierDistributionSelectorData;
  expeditionName?: string;
  copyCarPlates?: boolean;
  newTimeSlots?: Map<string, DuplicationMoreReservationTimeslotsDTO>;
}

/** The parts of the logged-in user the distribution defaults read. */
export type DuplicationUser = Pick<UserInfoOutDTO, 'defaultTimezoneId' | 'defaultCurrencyIsoCode'>;

/** Company and user settings the distribution starts from. */
export interface DistributionDefaults {
  defaultResponseDeadlineMode: 'relative' | 'absolute';
  distributionHours: number;
  loggedUser: DuplicationUser;
  currencyLockEnabled: boolean;
}

/**
 * Returns the list of fully-filled new timeslots ready to send to the backend.
 * A timeslot is ready when the common required fields (locationId, rampId, id) are present AND either:
 *   - new fields: utcStart + utcEnd + timezoneId (preferred), OR
 *   - legacy fields: sinceHourString + durationInMinutes + localDate
 */
export const resolveNewTimeslots = (slots: Map<string, DuplicationMoreReservationTimeslotsDTO> | undefined) => {
  if (!slots || Array.from(slots.values()).length === 0) return undefined;
  const ready = Array.from(slots.values()).filter(t => {
    if (!t.locationId || !t.rampId) return false;
    const hasNew = isNotNullOrUndefined(t.utcStart) && isNotNullOrUndefined(t.utcEnd) && isNotNullOrUndefined(t.timezoneId);
    const hasLegacy = !!(t.sinceHourString && t.durationInMinutes && t.localDate);
    return hasNew || hasLegacy;
  });
  return ready.length > 0 ? ready : undefined;
};

/** The distribution the duplicates are sent out with; null when they are not assigned. */
export const resolveDistribution = (data: Form, loggedUser: DuplicationUser): SaveDistributionRequestInDTO => {
  if (data.distribution.distributionType === DistributionType.DO_NOT_ASSIGN) {
    return null;
  }
  if (data.distribution.distributionType === DistributionType.ASSIGN) {
    return {
      directAssign: {
        price: data.distribution.price,
        carrierId: data.distribution.selectedItems[0].id,
        notifyAssignedCarrier: data.distribution.notifyAssignedCarrier,
        userIds: getSelectedUserIds(data.distribution.selectedItems),
      },
    };
  }
  if (data.distribution.distributionType === DistributionType.SPOT || data.distribution.distributionType === DistributionType.FIXED) {
    const dachserItem = data.distribution?.selectedItems?.find(item => item.apiTypes?.includes(ApiTypesEnum.DACHSER));
    const schenkerItem = data.distribution?.selectedItems?.find(item => item.apiTypes?.includes(ApiTypesEnum.SCHENKER));
    const resolvedDeadline = resolveResponseDeadline(data.distribution, loggedUser.defaultTimezoneId);

    const request = {
        price: data.distribution.price,
        responseDeadlineLocalTime: apiPragueFromDayjsLocalWithFormat(resolvedDeadline.localTime, resolvedDeadline.timezone),
        responseDeadlineTimezone: resolvedDeadline.timezone,
        utcEnd: apiUtcFromDayjs(resolvedDeadline.localTime),
        timezoneId: resolvedDeadline.timezone,
        selectedItems: data.distribution.selectedItems.map(carrier => {
          let selectedItemType: SelectedItemTypeEnum;
          switch (carrier.carrierType) {
            case 'GROUP':
              selectedItemType = SelectedItemTypeEnum.GROUP;
              break;
            case FindCarrierTypeEnum.API:
              selectedItemType = SelectedItemTypeEnum.API;
              break;
            default:
              selectedItemType = SelectedItemTypeEnum.CARRIER;
          }
          return {
            id: carrier.id,
            carrierOrGroup: carrier.carrierType === 'CARRIER' || carrier.carrierType === 'CARRIER_USER'
              ? CarrierOrGroupEnum.CARRIER
              : CarrierOrGroupEnum.GROUP,
            selectedItemType: selectedItemType,
            userId: Array.isArray(carrier.userIds) && carrier.userIds.length > 0 ? carrier.userIds[0] : null,
          };
        }),
        currencyLockType: data.distribution.currencyLockType,
        additionalData: {
          dachserProductType: dachserItem?._dachserProductTypeCode as DachserProductTypeEnum,
          dachserDeliveryTerm: dachserItem?._dachserDeliveryTermCode as DachserDeliveryTermEnum,
          schenkerProductType: schenkerItem?._schenkerProductTypeCode as SchenkerProductTypeEnum,
          schenkerAddonsType: schenkerItem?._schenkerAddonsTypeCodes as SchenkerAddonsTypeEnum[],
        },
        allowPartialDelivery: data.distribution.ladenFactor ? data.distribution.allowPartialDelivery : null,
        ladenFactor: data.distribution.ladenFactor ? data.distribution.ladenFactorValue : null,
    };
    return data.distribution.distributionType === DistributionType.FIXED
      ? { fixedRequest: request }
      : { spotRequest: request };
  }
};

/** The body of `saveDuplicationExpedition` / `createDuplicationJob`. */
export const buildDuplicationRequest = (data: Form, loggedUser: DuplicationUser) => {
  const body = {
    fromExpeditionId: data.fromExpeditionId,
    duplicateReservations: data.duplicateReservations,
    copyAttachments: data.copyAttachments,
    distribution: resolveDistribution(data, loggedUser),
    copyToLocalDates: data.copyToLocalDates,
    expeditionName: data.expeditionName,
    copyCarPlates: data.copyCarPlates,
    newTimeslots: resolveNewTimeslots(data.newTimeSlots),
    copyLabels: true,
    prefixApplicationId: true,
  };
  return body;
};

/** The distribution the form starts with: not assigned, with the company's deadline defaults. */
export const initDistribution = ({ defaultResponseDeadlineMode, distributionHours, loggedUser, currencyLockEnabled }: DistributionDefaults): CarrierDistributionSelectorData => {
  return {
    distributionType: DistributionType.DO_NOT_ASSIGN,
    selectedItems: [],
    responseDeadlineMode: defaultResponseDeadlineMode,
    responseDeadlineOffsetValue: distributionHours,
    responseDeadlineOffsetUnit: 'hours',
    responseDeadlineLocalTime:
      defaultResponseDeadlineMode === 'absolute'
        ? dayjsNowInTz(loggedUser.defaultTimezoneId).add(distributionHours, 'hour')
        : undefined,
    responseDeadlineTimezone: loggedUser.defaultTimezoneId,
    price: { currency: loggedUser?.defaultCurrencyIsoCode, price: 0 },
    notifyAssignedCarrier: false,
    currencyLockType: currencyLockEnabled ? CurrencyLockTypeEnum.ONLY_SELECTED : CurrencyLockTypeEnum.NONE,
  };
};

/** The original expedition's laden factor settings, carried into the reused distribution. */
export interface OriginalLadenFactor {
  originalLadenFactor?: number;
  originalAllowPartialDelivery?: boolean;
}

/** The original expedition's distribution, reused for the duplicates; null when it had none. */
export const getOriginalDistribution = (
  distributionType: DistributionType | undefined,
  duplicationCheck: CheckDuplicationExpeditionResponseOutDTO | undefined,
  { defaultResponseDeadlineMode, distributionHours, loggedUser, currencyLockEnabled }: DistributionDefaults,
  { originalLadenFactor, originalAllowPartialDelivery }: OriginalLadenFactor,
): CarrierDistributionSelectorData | null => {
  if (!duplicationCheck?.distribution) return null;

  const dist = duplicationCheck.distribution;

  const fixedOrSpot = dist.spot || dist.fixed;
  const price = fixedOrSpot?.price || dist.assignedPrice;

  const selectedItems = fixedOrSpot?.selectedItems?.map(item => ({
    id: item.id,
    userIds: item.userId ? [item.userId] : null,
    apiTypes: item.apiType ? [item.apiType as unknown as ApiTypesEnum] : null,
    carrierType: item.selectedItemType === SelectedItemTypeEnum.API
      ? FindCarrierTypeEnum.API
      : item.selectedItemType === SelectedItemTypeEnum.GROUP
        ? FindCarrierTypeEnum.GROUP
        : item.selectedItemType === SelectedItemTypeEnum.CARRIER && item.userId && item.userId.length > 0
          ? FindCarrierTypeEnum.CARRIER_USER
          : FindCarrierTypeEnum.CARRIER,
    _dachserProductTypeCode: fixedOrSpot.dachserProductType,
    _dachserDeliveryTermCode: fixedOrSpot.dachserDeliveryTerm,
    _schenkerProductTypeCode: fixedOrSpot.schenkerProductType,
    _schenkerAddonsTypeCodes: fixedOrSpot.schenkerAddonsType,
  })) || (dist.assignedCarrierId ? [{ id: dist.assignedCarrierId }] : []);

  return {
    distributionType: distributionType || DistributionType.DO_NOT_ASSIGN,
    price,
    selectedItems,
    responseDeadlineMode: defaultResponseDeadlineMode,
    responseDeadlineOffsetValue: distributionHours,
    responseDeadlineOffsetUnit: 'hours' as const,
    responseDeadlineLocalTime:
      defaultResponseDeadlineMode === 'absolute'
        ? dayjsNowInTz(loggedUser.defaultTimezoneId ?? 'Europe/Prague').add(distributionHours, 'hour')
        : undefined,
    responseDeadlineTimezone: loggedUser.defaultTimezoneId ?? 'Europe/Prague',
    notifyAssignedCarrier: false,
    currencyLockType: currencyLockEnabled ? CurrencyLockTypeEnum.ONLY_SELECTED : CurrencyLockTypeEnum.NONE,
    ladenFactor: originalLadenFactor > 0,
    ladenFactorValue: originalLadenFactor,
    allowPartialDelivery: originalAllowPartialDelivery,
  };
};

/** A new timeslot after a free slot was picked from the offer: its start, ramp and timezone. */
export const applySlotToTimeslot = (
  current: DuplicationMoreReservationTimeslotsDTO | undefined,
  slot: TimeslotCalendarDTO,
  rampId: string,
): DuplicationMoreReservationTimeslotsDTO => {
  const slotTz = (isNotNullOrUndefined(slot.utcStart) && isNotNullOrUndefined(slot.timezoneId)) ? slot.timezoneId : 'Europe/Prague';
  const slotSince = (isNotNullOrUndefined(slot.utcStart) && isNotNullOrUndefined(slot.timezoneId)) ? slot.utcStart : slot.since;
  const utcStart = isNotNullOrUndefined(slot.utcStart) ? slot.utcStart : null;
  const utcEnd = utcStart && current?.durationInMinutes
    ? apiUtcFromDayjs(dayjsFromApiUtcInTz(utcStart, slotTz).add(current.durationInMinutes, 'minute'))
    : isNotNullOrUndefined(slot.utcEnd) ? slot.utcEnd : null;
  const updated: DuplicationMoreReservationTimeslotsDTO = {
    ...current,
    sinceHourString: stringHHmmFromStringInTz(slotSince, slotTz),
    utcStart,
    utcEnd,
    timezoneId: isNotNullOrUndefined(slot.timezoneId) ? slot.timezoneId : null,
    rampId,
  };
  return updated;
};
